import React, { useState, useEffect, useRef } from "react";
import smoothscroll from "smoothscroll-polyfill";
import { useKitchen } from "../../context/KitchenContext";
import BrowseKitchensSkeleton from "./Skeleton/BrowseKitchensSkeleton";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import FilterModal from "../../components/modals/modalVariants/FilterModal";
import KitchenCard from "./KitchenCard/KitchenCard";
import MapComponent from "../../utils/MapComponent/MapComponent";
import EstimateEarnings from "../Listing/EarningsCalculator.js";
import Testimonials from "./Testimonials";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ToastContainer, toast } from "react-toastify";
import { Button, IconButton, CircularProgress, Box } from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import {
  faMapMarkerAlt,
  faFilterCircleDollar,
} from "@fortawesome/free-solid-svg-icons";
import { BiCurrentLocation } from "react-icons/bi";
import { haversineDistance } from "../../utils/distance";
import config from "../../config";
import "./BrowseKitchens.css";
import { motion } from "framer-motion";
import { FaRegSmile } from "react-icons/fa";
import { BsChatDots } from "react-icons/bs";

const loadGoogleMapsScript = (() => {
  let scriptLoadingPromise;
  return () => {
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }
    if (scriptLoadingPromise) {
      return scriptLoadingPromise;
    }
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    if (existingScript) {
      scriptLoadingPromise = new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          existingScript.addEventListener("load", () => resolve());
          existingScript.addEventListener("error", (err) => reject(err));
        }
      });
      return scriptLoadingPromise;
    }
    scriptLoadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.mapApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);
      document.head.appendChild(script);
    });
    return scriptLoadingPromise;
  };
})();

// Add Capacitor Logger import
const loadCapacitorLogger = async () => {
  if (window.Capacitor) {
    const { Logger } = await import("@capacitor/core");
    return Logger;
  }
  return null;
};

const BrowseKitchens = () => {
  const { fetchAllKitchens, searchKitchens } = useKitchen();
  const [filters, setFilters] = useState({
    location: "",
    latitude: null,
    longitude: null,
    kitchenCategory: "",
    kitchenSize: "",
    checkinDate: null,
    checkoutDate: null,
    priceOrder: "asc",
    bookingType: "",
  });
  const [displayKitchens, setDisplayKitchens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const kitchenListRef = useRef(null);
  const locationInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const location = useLocation();
  const [showFilters, setShowFilters] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchBarHeight, setSearchBarHeight] = useState(0);
  const searchBarRef = useRef(null);
  const [stickyThreshold, setStickyThreshold] = useState(
    window.innerWidth < 768 ? 100 : 240
  );
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const isCapacitor = !!window.Capacitor;

  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message, { icon: "💖" });
    } else if (type === "info") {
      toast.info(message);
    } else if (type === "error") {
      toast.error(message);
    }
  };

  useEffect(() => {
    smoothscroll.polyfill();
  }, []);

  useEffect(() => {
    const updateThreshold = () => {
      setStickyThreshold(window.innerWidth < 768 ? 100 : 240);
    };
    updateThreshold();
    window.addEventListener("resize", updateThreshold);
    return () => window.removeEventListener("resize", updateThreshold);
  }, []);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          setIsSticky(scrollPosition > stickyThreshold);
          setIsScrolled(scrollPosition > stickyThreshold + 35);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [stickyThreshold]);

  useEffect(() => {
    const updateSearchBarHeight = () => {
      if (searchBarRef.current) {
        setSearchBarHeight(searchBarRef.current.getBoundingClientRect().height);
      }
    };
    updateSearchBarHeight();
    window.addEventListener("resize", updateSearchBarHeight);
    return () => window.removeEventListener("resize", updateSearchBarHeight);
  }, [isSticky]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {
      location: searchParams.get("location") || "",
      latitude: searchParams.get("latitude")
        ? parseFloat(searchParams.get("latitude"))
        : null,
      longitude: searchParams.get("longitude")
        ? parseFloat(searchParams.get("longitude"))
        : null,
      kitchenCategory: searchParams.get("kitchenCategory") || "",
      kitchenSize: searchParams.get("kitchenSize") || "",
      checkinDate: searchParams.get("checkinDate")
        ? new Date(searchParams.get("checkinDate"))
        : null,
      checkoutDate: searchParams.get("checkoutDate")
        ? new Date(searchParams.get("checkoutDate"))
        : null,
      priceOrder: searchParams.get("priceOrder") || "asc",
      bookingType: searchParams.get("bookingType") || "",
    };
    setFilters(params);

    const shouldFetchAll =
      params.latitude != null || params.kitchenCategory !== "";
    if (!shouldFetchAll) {
      loadAllKitchens();
    } else {
      handleSearch(params);
    }
  }, [location.search]);

  useEffect(() => {
    if (!showMap || !mapRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !mapVisible) {
          setMapVisible(true);
          loadGoogleMapsScript()
            .then(() => {
              setIsScriptLoaded(true);
              initializeAutocomplete();
            })
            .catch((error) => {
              console.error("Error loading Google Maps script:", error);
              showToast("Failed to load map", "error");
            });
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0,
      }
    );

    observer.observe(mapRef.current);
    return () => {
      if (mapRef.current) observer.unobserve(mapRef.current);
    };
  }, [showMap]);

  const initializeAutocomplete = () => {
    if (locationInputRef.current && window.google && window.google.maps) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        locationInputRef.current,
        { types: ["geocode"] }
      );
      autocompleteRef.current.setFields(["geometry", "formatted_address"]);
      autocompleteRef.current.addListener("place_changed", handlePlaceSelect);
    }
  };

  const loadAllKitchens = async () => {
    setLoading(true);
    try {
      const allKitchens = await fetchAllKitchens();
      setDisplayKitchens(allKitchens);
    } catch (error) {
      console.error("Error loading all kitchens:", error);
      setDisplayKitchens([]);
      showToast("Failed to load kitchens", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceSelect = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        const formattedAddress = place.formatted_address;
        setFilters((prevFilters) => ({
          ...prevFilters,
          location: formattedAddress,
          latitude,
          longitude,
        }));
        handleSearch({
          ...filters,
          location: formattedAddress,
          latitude,
          longitude,
        });
      }
    }
  };

  const handleLocationInputFocus = () => {
    if (window.google && window.google.maps) {
      if (!autocompleteRef.current) {
        initializeAutocomplete();
      }
    } else {
      loadGoogleMapsScript()
        .then(() => {
          setIsScriptLoaded(true);
          initializeAutocomplete();
        })
        .catch((error) => {
          console.error("Error loading Google Maps script:", error);
        });
    }
    getUserLocation();
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      setLoadingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLoadingLocation(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          showToast("Unable to retrieve your location", "error");
          setLoadingLocation(false);
        }
      );
    } else {
      showToast("Geolocation is not supported by your browser", "error");
      setLoadingLocation(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (autocompleteRef.current) {
        handlePlaceSelect();
      }
    }
  };

  const handleGeoButtonClick = async () => {
    setLoadingLocation(true);
    try {
      let latitude, longitude;

      // Initialize Capacitor Logger
      const Logger = await loadCapacitorLogger();
      const log = (message, data = null) => {
        if (Logger) {
          Logger.log({
            message: data ? `${message}: ${JSON.stringify(data)}` : message,
            level: "info",
          });
        } else {
          console.log(message, data);
        }
      };

      if (isCapacitor) {
        try {
          log("Attempting to use Capacitor Geolocation...");
          // Import Capacitor Geolocation
          const { Geolocation } = await import("@capacitor/geolocation");
          log("Capacitor Geolocation imported successfully");

          // Check if permissions are granted
          const permissionStatus = await Geolocation.checkPermissions();
          log("Permission status:", permissionStatus);

          if (permissionStatus.location !== "granted") {
            log("Requesting location permissions...");
            const requestStatus = await Geolocation.requestPermissions();
            log("Permission request status:", requestStatus);

            if (requestStatus.location !== "granted") {
              throw new Error("Location permission denied");
            }
          }

          log("Getting current position...");
          // Get current position with options
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 0,
          });

          log("Position received:", position);
          if (!position || !position.coords) {
            throw new Error("Invalid position data received");
          }

          latitude = position.coords.latitude;
          longitude = position.coords.longitude;

          log("Coordinates:", { latitude, longitude });
        } catch (capacitorError) {
          log("Detailed Capacitor Geolocation error:", {
            name: capacitorError.name,
            message: capacitorError.message,
            stack: capacitorError.stack,
          });

          // Try fallback to browser geolocation if Capacitor fails
          if (navigator.geolocation) {
            log("Falling back to browser geolocation...");
            await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  latitude = position.coords.latitude;
                  longitude = position.coords.longitude;
                  log("Browser fallback coordinates:", { latitude, longitude });
                  resolve();
                },
                (error) => {
                  log("Browser Geolocation error:", error);
                  reject(
                    new Error("Unable to retrieve location. Check permissions.")
                  );
                },
                {
                  enableHighAccuracy: true,
                  timeout: 30000,
                  maximumAge: 0,
                }
              );
            });
          } else {
            throw new Error("Failed to get location from device");
          }
        }
      } else if (navigator.geolocation) {
        // Use browser geolocation for web
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              resolve();
            },
            (error) => {
              log("Browser Geolocation error:", error);
              reject(
                new Error("Unable to retrieve location. Check permissions.")
              );
            },
            {
              enableHighAccuracy: true,
              timeout: 30000,
              maximumAge: 0,
            }
          );
        });
      } else {
        throw new Error("Geolocation is not supported by your browser");
      }

      if (!latitude || !longitude) {
        throw new Error("Invalid coordinates received");
      }

      log("Final coordinates to use:", { latitude, longitude });

      // Ensure Google Maps script is loaded
      if (!window.google || !window.google.maps) {
        log("Loading Google Maps script...");
        await loadGoogleMapsScript();
        setIsScriptLoaded(true);
        initializeAutocomplete();
      }

      const geocoder = new window.google.maps.Geocoder();
      const latLng = { lat: parseFloat(latitude), lng: parseFloat(longitude) };

      log("Attempting to geocode coordinates:", latLng);

      geocoder.geocode({ location: latLng }, (results, status) => {
        log("Geocoding results:", { status, results });
        if (status === "OK" && results?.length > 0) {
          const formattedAddress = results[0].formatted_address;
          const newFilters = {
            ...filters,
            location: formattedAddress,
            latitude,
            longitude,
          };
          setFilters(newFilters);
          handleSearch(newFilters);
        } else {
          log("Geocoder failed:", status);
          showToast("Unable to find location for coordinates", "error");
        }
        setLoadingLocation(false);
      });
    } catch (error) {
      const Logger = await loadCapacitorLogger();
      if (Logger) {
        Logger.log({
          message: `Error in handleGeoButtonClick: ${error.message}`,
          level: "error",
        });
      } else {
        console.error("Error in handleGeoButtonClick:", error);
      }
      showToast(
        error.message || "Unable to retrieve location. Check permissions.",
        "error"
      );
      setLoadingLocation(false);
    }
  };

  const handleSearch = async (searchFilters = filters) => {
    const {
      location,
      latitude,
      longitude,
      kitchenCategory,
      kitchenSize,
      checkinDate,
      checkoutDate,
      priceOrder,
      bookingType,
    } = searchFilters;

    const hasLocation = latitude !== null && longitude !== null;
    const hasKitchenCategory = kitchenCategory.trim() !== "";

    if (!hasLocation && !bookingType && !hasKitchenCategory) {
      loadAllKitchens();
      return;
    }

    setFilterLoading(true);

    try {
      const cleanedFilters = {};
      if (hasLocation) {
        cleanedFilters.latitude = latitude;
        cleanedFilters.longitude = longitude;
      }
      if (hasKitchenCategory) {
        cleanedFilters.kitchenCategory = kitchenCategory;
      }
      if (kitchenSize) {
        cleanedFilters.kitchenSize = kitchenSize;
      }
      if (checkinDate) {
        cleanedFilters.checkinDate = checkinDate.toISOString();
      }
      if (checkoutDate) {
        cleanedFilters.checkoutDate = checkoutDate.toISOString();
      }
      if (priceOrder) {
        cleanedFilters.priceOrder = priceOrder;
      }
      if (bookingType) {
        cleanedFilters.bookingType = bookingType;
      }

      const filteredKitchens = (await searchKitchens(cleanedFilters)) || [];
      const kitchensWithDistance = filteredKitchens.map((kitchen) => {
        if (hasLocation) {
          const distance = haversineDistance(
            latitude,
            longitude,
            kitchen.Latitude,
            kitchen.Longitude
          );
          return { ...kitchen, distance: distance.toFixed(2) };
        }
        return { ...kitchen, distance: null };
      });
      setDisplayKitchens(kitchensWithDistance);
      if (kitchenListRef.current) {
        const offsetTop = kitchenListRef.current.offsetTop;
        window.scrollTo({ top: offsetTop - 100, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error during search:", error);
      setDisplayKitchens([]);
      showToast("Failed to search kitchens", "error");
    } finally {
      setFilterLoading(false);
    }
  };

  const handleApplyFilters = () => {
    handleSearch(filters);
    setShowFilters(false);
  };

  return (
    <>
      {loading && <BrowseKitchensSkeleton />}
      <div className={`browse-page ${loading ? "hidden" : ""}`}>
        <Navbar />
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">Look. Book. Cook.</h1>
            <p className="hero-subtitle">
              Rent restaurant kitchens during off-hours. Launch and grow,
              anytime anywhere.
            </p>
            <div
              ref={searchBarRef}
              className={`search-bar ${isSticky ? "sticky" : ""}`}
            >
              <motion.div
                className="browse-search-bar"
                initial={{ opacity: 0, scale: 0.1 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="autocomplete-container">
                  <IconButton
                    onClick={handleGeoButtonClick}
                    disabled={loadingLocation || filterLoading}
                    aria-label="Use my current location"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      borderRadius: "50%",
                      p: { xs: 0.5, sm: 1 },
                      mr: { xs: 0.5, sm: 1 },
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    {loadingLocation ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <BiCurrentLocation size={18} />
                    )}
                  </IconButton>
                  <input
                    type="text"
                    ref={locationInputRef}
                    placeholder="Where do you want to cook, Chef?"
                    value={filters.location}
                    onChange={(e) =>
                      setFilters((prevFilters) => ({
                        ...prevFilters,
                        location: e.target.value,
                        latitude: null,
                        longitude: null,
                      }))
                    }
                    onFocus={handleLocationInputFocus}
                    onKeyDown={handleKeyDown}
                    disabled={filterLoading}
                  />
                  {filters.location && (
                    <IconButton
                      aria-label="Clear location"
                      onClick={() => {
                        const clearedFilters = {
                          ...filters,
                          location: "",
                          latitude: null,
                          longitude: null,
                        };
                        setFilters(clearedFilters);
                        loadAllKitchens();
                      }}
                      sx={{
                        color: "#999",
                        ml: 1,
                        "&:hover": { color: "#e63946" },
                      }}
                    >
                      ×
                    </IconButton>
                  )}
                  {filterLoading && (
                    <CircularProgress size={18} sx={{ ml: 1 }} />
                  )}
                </div>
              </motion.div>
              <div className="category-bar-container">
                <div className="category-bar-1">
                  {[
                    { label: "All", value: "" },
                    { label: "Restaurant Kitchen", value: "restaurant" },
                    { label: "Commercial Kitchen", value: "commercial" },
                    { label: "Ghost Kitchen", value: "ghost" },
                    { label: "Bakery Kitchen", value: "bakery" },
                    { label: "All purpose Kitchen", value: "all" },
                    { label: "Commissary Kitchen", value: "commissary" },
                    { label: "Prep Kitchen", value: "prep" },
                  ].map(({ label, value }) => (
                    <button
                      key={value}
                      className={`category-button ${
                        filters.kitchenCategory === value ? "active" : ""
                      }`}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          kitchenCategory: value,
                        };
                        setFilters(newFilters);
                        handleSearch(newFilters);
                      }}
                      disabled={filterLoading}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className={`buttons ${isScrolled ? "scrolled" : ""}`}>
                  <div className="action-buttons">
                    <Button
                      className="icon-button"
                      onClick={() => setShowFilters(true)}
                      aria-label="Filter Options"
                      title="Filter Options"
                      disabled={filterLoading}
                      startIcon={
                        <FontAwesomeIcon
                          icon={faFilterCircleDollar}
                          className="text-gray-700"
                          style={{
                            fontSize:
                              window.innerWidth <= 768 ? "14px" : "18px",
                          }}
                        />
                      }
                      sx={{
                        backgroundColor: "#2c3e50",
                        color: "white",
                        borderRadius: "50px",
                        px: { xs: 2, sm: 3 },
                        py: 0.5,
                        "&:hover": { backgroundColor: "primary.dark" },
                      }}
                    >
                      <div className="text">Filter</div>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {isSticky && (
          <div
            className="sticky-placeholder"
            style={{ height: searchBarHeight }}
          />
        )}
        <FilterModal
          open={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          setFilters={setFilters}
          onApplyFilters={handleApplyFilters}
        />
        <section className="kitchen-section" ref={kitchenListRef}>
          <Box className="kitchen-list" sx={{ position: "relative" }}>
            {filterLoading && (
              <Box className="loading-overlay">
                <CircularProgress size={50} />
                <p className="loading-text">
                  Finding the perfect kitchen for you...
                </p>
              </Box>
            )}
            {loading ? (
              <Box className="loading-container">
                <CircularProgress size={50} />
                <p className="loading-text">
                  Looking the perfect kitchen for you...
                </p>
              </Box>
            ) : displayKitchens.length > 0 ? (
              displayKitchens.map((kitchen) => (
                <div className="kitchen-card-container" key={kitchen.KitchenID}>
                  <KitchenCard
                    kitchen={kitchen}
                    distance={kitchen.distance}
                    showToast={showToast}
                  />
                </div>
              ))
            ) : (
              <div className="no-kitchen-results">
                <FaRegSmile size={50} color="#e63946" />
                <p>No kitchens found matching your criteria.</p>
              </div>
            )}
          </Box>
        </section>
        {showMap && (
          <section className="map-section" ref={mapRef}>
            <div className="map-container">
              {mapVisible && isScriptLoaded ? (
                <MapComponent kitchens={displayKitchens} />
              ) : (
                <Box className="loading-container">
                  <CircularProgress size={50} />
                  <p className="loading-text">Loading map...</p>
                </Box>
              )}
            </div>
          </section>
        )}
        <EstimateEarnings />
        <Testimonials />
        <div className="floating-map-button">
          <Button
            className="map-float-btn"
            onClick={() => {
              if (showMap) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                setTimeout(() => {
                  if (mapRef.current) {
                    const navbar = document.querySelector(".navbar");
                    const navbarHeight = navbar ? navbar.offsetHeight : 0;
                    const elementRect = mapRef.current.getBoundingClientRect();
                    const elementTop = elementRect.top + window.pageYOffset;
                    const scrollToPosition = elementTop - navbarHeight - 30;
                    window.scrollTo({
                      top: scrollToPosition,
                      behavior: "smooth",
                    });
                  }
                }, 100);
              }
              setShowMap(!showMap);
            }}
            aria-label="Toggle Map"
            title="Toggle Map"
            startIcon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              borderRadius: "50px",
              px: { xs: 2, sm: 3 },
              py: 0.5,
              "&:hover": { backgroundColor: "primary.dark" },
            }}
          >
            {showMap ? "Hide Map" : "Show Map"}
          </Button>
        </div>
        <Footer />
        <ToastContainer position="top-right" autoClose={2000} />
        {window.innerWidth <= 768 && (
          <button
            className="floating-intercom-button"
            onClick={() => window.Intercom("show")}
          >
            <BsChatDots size={28} />
          </button>
        )}
      </div>
    </>
  );
};

export default BrowseKitchens;
